import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DataContext from './dataContext';
import {setActiveUserId, resetActiveUserToLocal} from '../database/currentUser';
import {parseAuthParametersFromUrl} from '../config/authRedirect';
import {
  getPasswordResetRedirectUrl,
  supabase,
  isSupabaseConfigured,
} from '../config/supabaseClient';
import {subscribeAuthDeepLink} from '../services/authDeepLink';
import {persistSupabaseUserToLocalDb} from '../services/authSession';
import {
  deleteRemoteUserLocalData,
  mergeLocalDefaultUserIntoRemote,
} from '../services/localUserMerge';
import {
  applyUserSettingsFromDbToApp,
  migrateAsyncStorageToUserSettingsDb,
} from '../services/userSettingsSync';
import {
  pullReadingPlanProgressFromRemote,
  pushUserFavoritesToRemote,
  pushUserNotesToRemote,
  pushUserReadingProgressToRemote,
  syncUserData,
  syncUserSettingsOnly,
} from '../services/syncService';
import {registerUserDataRemoteSync} from '../services/userDataRemoteSync';
import {registerThemePersistHook} from '../utils/storage';
import {IS_WEB} from '../utils/platform';

/** Avoid hanging UI if GoTrue/network never completes `signOut()`. */
const SIGN_OUT_REMOTE_TIMEOUT_MS = 12000;
const SIGN_OUT_LOCAL_TIMEOUT_MS = 8000;
/** Password reset email / update password can block on slow or stuck HTTP. */
const AUTH_PASSWORD_FLOW_TIMEOUT_MS = 30000;
/** Login/register should fail fast instead of hanging the spinner forever. */
const AUTH_SIGN_IN_UP_TIMEOUT_MS = 30000;

function withTimeout(promise, ms, timeoutMessage = 'Request timed out') {
  let timeoutId;
  return new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      reject(Object.assign(new Error(timeoutMessage), {name: 'TimeoutError'}));
    }, ms);
    promise.then(
      value => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      err => {
        clearTimeout(timeoutId);
        reject(err);
      },
    );
  });
}

const AuthContext = createContext({
  session: null,
  user: null,
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  syncNow: async () => {},
  resetPasswordForEmail: async () => {},
  updatePassword: async () => {},
  passwordRecovery: false,
  isConfigured: false,
  /** True after explicit sign-out until next app restart; hides first-launch auth prompt. */
  suppressAuthPromptAfterSignOut: false,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({children}) {
  const {db} = useContext(DataContext);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suppressAuthPromptAfterSignOut, setSuppressAuthPromptAfterSignOut] =
    useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  /** Retained after session clears so SIGNED_OUT can remove SQLite rows for that account. */
  const lastAuthenticatedUserIdRef = useRef(null);
  /** Native deep link: recovery via setSession emits SIGNED_IN, not PASSWORD_RECOVERY — skip applySession until password saved. */
  const nativeRecoveryPendingRef = useRef(false);
  const lastAuthDeepLinkUrlRef = useRef('');
  const authDeepLinkInFlightRef = useRef(false);

  const handleAuthDeepLink = useCallback(async url => {
    if (!supabase || !url || authDeepLinkInFlightRef.current) {
      return;
    }
    if (url === lastAuthDeepLinkUrlRef.current) {
      return;
    }
    const params = parseAuthParametersFromUrl(url);
    if (!params.access_token || !params.refresh_token) {
      return;
    }
    authDeepLinkInFlightRef.current = true;
    try {
      if (params.type === 'recovery') {
        nativeRecoveryPendingRef.current = true;
      }
      const {error} = await supabase.auth.setSession({
        access_token: params.access_token,
        refresh_token: params.refresh_token,
      });
      if (error) {
        nativeRecoveryPendingRef.current = false;
        console.warn('Auth deep link: setSession failed', error);
        return;
      }
      lastAuthDeepLinkUrlRef.current = url;
    } finally {
      authDeepLinkInFlightRef.current = false;
    }
  }, []);

  const applySession = useCallback(
    async nextSession => {
      if (!db || !nextSession?.user) {
        setSession(nextSession);
        return;
      }
      const {user} = nextSession;
      await persistSupabaseUserToLocalDb(db, user);
      setActiveUserId(user.id);
      await migrateAsyncStorageToUserSettingsDb(db, user.id);
      let readingPlanPullOk = true;
      if (supabase) {
        // Pull before merge so Supabase progress replaces local rows (post-logout zeros) instead of merge+push wiping the server.
        readingPlanPullOk = await pullReadingPlanProgressFromRemote(
          supabase,
          db,
          user.id,
        );
      }
      await mergeLocalDefaultUserIntoRemote(db, user.id);
      if (supabase) {
        await syncUserData(supabase, db, user.id, {
          skipPullReadingPlanProgress: true,
          readingPlanPullOk,
        });
      }
      await applyUserSettingsFromDbToApp(db, user.id);
      setSession(nextSession);
    },
    [db],
  );

  useEffect(() => {
    if (session?.user?.id) {
      lastAuthenticatedUserIdRef.current = session.user.id;
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setLoading(false);
      return undefined;
    }

    const {
      data: {subscription},
    } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
      if (event === 'PASSWORD_RECOVERY') {
        if (nextSession?.user) {
          setSession(nextSession);
          setPasswordRecovery(true);
        }
        setLoading(false);
        return;
      }
      if (event === 'SIGNED_IN' && nativeRecoveryPendingRef.current) {
        nativeRecoveryPendingRef.current = false;
        if (nextSession?.user) {
          setSession(nextSession);
          setPasswordRecovery(true);
        }
        setLoading(false);
        return;
      }
      if (event === 'INITIAL_SESSION') {
        if (nextSession?.user) {
          const hash = IS_WEB ? globalThis.location?.hash ?? '' : '';
          const isRecoveryFromUrl =
            hash &&
            (hash.includes('type=recovery') ||
              new URLSearchParams(hash.replace(/^#/, '')).get('type') ===
                'recovery');
          if (isRecoveryFromUrl) {
            setSession(nextSession);
            setPasswordRecovery(true);
          } else {
            await applySession(nextSession);
          }
        } else {
          setSession(null);
        }
        setLoading(false);
        return;
      }
      if (event === 'SIGNED_OUT') {
        const uid =
          lastAuthenticatedUserIdRef.current ?? nextSession?.user?.id;
        lastAuthenticatedUserIdRef.current = null;
        resetActiveUserToLocal();
        setSession(null);
        setPasswordRecovery(false);
        lastAuthDeepLinkUrlRef.current = '';
        setSuppressAuthPromptAfterSignOut(true);
        setLoading(false);
        // Defer SQLite cleanup + IndexedDB export so the auth callback returns quickly;
        // awaiting here can block `signOut()` and duplicate work already queued.
        if (db && uid) {
          void deleteRemoteUserLocalData(db, uid).catch(e => {
            console.warn('SIGNED_OUT: local user data delete failed', e);
          });
        }
        return;
      }
      // Token refresh must not run full applySession (sync/pull); Supabase serializes
      // auth callbacks — a hung sync blocks signOut() and leaves the UI spinning.
      if (event === 'TOKEN_REFRESHED') {
        if (nextSession?.user) {
          setSession(nextSession);
        }
        setLoading(false);
        return;
      }
      if (event === 'USER_UPDATED') {
        setPasswordRecovery(false);
        setLoading(false);
        const s = nextSession;
        // Must not await applySession here: GoTrue awaits this callback while still holding the
        // auth mutex; applySession uses Supabase/DB and can re-enter auth → deadlock, so
        // updateUser() never resolves (spinner forever). Defer sync after the mutex is released.
        if (s?.user) {
          queueMicrotask(() => {
            void applySession(s).catch(e => {
              console.warn('USER_UPDATED: applySession failed', e);
            });
          });
        }
        return;
      }
      if (nextSession?.user) {
        await applySession(nextSession);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [applySession, db]);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase || IS_WEB) {
      return undefined;
    }
    return subscribeAuthDeepLink(url => {
      void handleAuthDeepLink(url);
    });
  }, [handleAuthDeepLink]);

  useEffect(() => {
    registerThemePersistHook(async () => {
      if (!session?.user || !supabase || !db) {
        return;
      }
      await syncUserSettingsOnly(supabase, db, session.user.id);
    });
    return () => registerThemePersistHook(null);
  }, [session, db]);

  useEffect(() => {
    registerUserDataRemoteSync(async kind => {
      if (!session?.user || !supabase || !db) {
        return;
      }
      const uid = session.user.id;
      if (kind === 'notes') {
        await pushUserNotesToRemote(supabase, db, uid);
      } else if (kind === 'favorites') {
        await pushUserFavoritesToRemote(supabase, db, uid);
      } else if (kind === 'progress') {
        await pushUserReadingProgressToRemote(supabase, db, uid);
      }
    });
    return () => registerUserDataRemoteSync(null);
  }, [session, db]);

  const signIn = useCallback(
    async (email, password) => {
      if (!supabase) {
        return {error: new Error('Supabase not configured'), needsEmailConfirmation: false};
      }
      setLoading(true);
      try {
        const {data, error} = await withTimeout(
          supabase.auth.signInWithPassword({
            email: email.trim(),
            password,
          }),
          AUTH_SIGN_IN_UP_TIMEOUT_MS,
        );
        // NOTE: The `applySession` is already triggered and awaited by the `onAuthStateChange` listener.
        return {error};
      } catch (e) {
        if (e?.name === 'TimeoutError') {
          const err = new Error('AUTH_REQUEST_TIMEOUT');
          err.code = 'AUTH_REQUEST_TIMEOUT';
          return {error: err};
        }
        return {error: e};
      } finally {
        setLoading(false);
      }
    },
    [applySession],
  );

  const signUp = useCallback(
    async (email, password) => {
      if (!supabase) {
        return {error: new Error('Supabase not configured'), needsEmailConfirmation: false};
      }
      setLoading(true);
      try {
        const {data, error} = await withTimeout(
          supabase.auth.signUp({
            email: email.trim(),
            password,
          }),
          AUTH_SIGN_IN_UP_TIMEOUT_MS,
        );
        // NOTE: If session is available, \`applySession\` is already handled by \`onAuthStateChange\`.
        return {error, needsEmailConfirmation: !data?.session};
      } catch (e) {
        if (e?.name === 'TimeoutError') {
          const err = new Error('AUTH_REQUEST_TIMEOUT');
          err.code = 'AUTH_REQUEST_TIMEOUT';
          return {error: err, needsEmailConfirmation: false};
        }
        return {error: e, needsEmailConfirmation: false};
      } finally {
        setLoading(false);
      }
    },
    [applySession],
  );

  const signOut = useCallback(async () => {
    if (!supabase) {
      resetActiveUserToLocal();
      setSession(null);
      setPasswordRecovery(false);
      lastAuthDeepLinkUrlRef.current = '';
      setSuppressAuthPromptAfterSignOut(true);
      return;
    }
    setLoading(true);
    try {
      let error;
      try {
        const out = await withTimeout(
          supabase.auth.signOut(),
          SIGN_OUT_REMOTE_TIMEOUT_MS,
        );
        error = out?.error;
      } catch (e) {
        if (e?.name === 'TimeoutError') {
          console.warn('signOut: remote sign-out timed out; clearing local session');
        } else {
          console.warn('signOut exception', e);
        }
        try {
          ({error} = await withTimeout(
            supabase.auth.signOut({scope: 'local'}),
            SIGN_OUT_LOCAL_TIMEOUT_MS,
          ));
        } catch (eLocal) {
          console.warn('signOut: local sign-out timed out or failed', eLocal);
        }
      }
      if (error) {
        try {
          ({error} = await withTimeout(
            supabase.auth.signOut({scope: 'local'}),
            SIGN_OUT_LOCAL_TIMEOUT_MS,
          ));
        } catch (eLocal) {
          console.warn('signOut: local retry timed out or failed', eLocal);
        }
      }
      if (error) {
        console.warn('signOut:', error);
      }
    } catch (e) {
      console.warn('signOut: final fallback', e);
      try {
        await withTimeout(
          supabase.auth.signOut({scope: 'local'}),
          SIGN_OUT_LOCAL_TIMEOUT_MS,
        );
      } catch (e2) {
        console.warn('signOut local scope failed', e2);
      }
    } finally {
      resetActiveUserToLocal();
      setSession(null);
      setPasswordRecovery(false);
      setSuppressAuthPromptAfterSignOut(true);
      setLoading(false);
    }
  }, []);

  const resetPasswordForEmail = useCallback(async email => {
    if (!supabase) {
      return {error: new Error('Supabase not configured')};
    }
    const redirectTo = getPasswordResetRedirectUrl();
    const options = redirectTo ? {redirectTo} : {};
    try {
      const {error} = await withTimeout(
        supabase.auth.resetPasswordForEmail(email.trim(), options),
        AUTH_PASSWORD_FLOW_TIMEOUT_MS,
      );
      return {error};
    } catch (e) {
      if (e?.name === 'TimeoutError') {
        const err = new Error('AUTH_REQUEST_TIMEOUT');
        err.code = 'AUTH_REQUEST_TIMEOUT';
        return {error: err};
      }
      return {error: e};
    }
  }, []);

  const updatePassword = useCallback(async newPassword => {
    if (!supabase) {
      return {error: new Error('Supabase not configured')};
    }
    setLoading(true);
    try {
      const {error} = await withTimeout(
        supabase.auth.updateUser({password: newPassword}),
        AUTH_PASSWORD_FLOW_TIMEOUT_MS,
      );
      return {error};
    } catch (e) {
      if (e?.name === 'TimeoutError') {
        const err = new Error('AUTH_REQUEST_TIMEOUT');
        err.code = 'AUTH_REQUEST_TIMEOUT';
        return {error: err};
      }
      return {error: e};
    } finally {
      setLoading(false);
    }
  }, []);

  const syncNow = useCallback(async () => {
    if (!supabase || !db || !session?.user) {
      return;
    }
    await syncUserData(supabase, db, session.user.id);
    await applyUserSettingsFromDbToApp(db, session.user.id);
  }, [db, session]);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signIn,
      signUp,
      signOut,
      syncNow,
      resetPasswordForEmail,
      updatePassword,
      passwordRecovery,
      isConfigured: isSupabaseConfigured,
      suppressAuthPromptAfterSignOut,
    }),
    [
      session,
      loading,
      signIn,
      signUp,
      signOut,
      syncNow,
      resetPasswordForEmail,
      updatePassword,
      passwordRecovery,
      suppressAuthPromptAfterSignOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
